import { useDeferredValue, useId, useMemo, useState } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { humanize } from './adminLabels';
import { inputCls } from './ui/styles';
import { fill } from '../utils/copy';
import { plural } from '../core/format/text';
import type { Json } from './ui/ContentEditor';
export type SearchablePage = {
  key: string;
  label: string;
  value: Json;
};
export type SearchHit = {
  page: string;
  block: string | null;
};
type Props = {
  pages: SearchablePage[];
  onPick: (hit: SearchHit) => void;
};
type Match = SearchHit & {
  pageLabel: string;
  where: string;
  text: string;
};
/** Eén tekst uit een pagina, al klaar om in te zoeken. */
type Entry = Match & {
  haystack: string;
};
const MAX_RESULTS = 8;
const MIN_QUERY = 3;

/** Alle tekst in een pagina, met het blok waar hij in staat. */
function collect(v: Json, block: string | null, field: string, out: {
  block: string | null;
  field: string;
  text: string;
}[]) {
  if (typeof v === 'string') out.push({
    block,
    field,
    text: v
  });else if (Array.isArray(v)) v.forEach(x => collect(x, block, field, out));else if (v && typeof v === 'object') for (const [k, x] of Object.entries(v)) collect(x, block ?? k, k, out);
}

/**
 * De zoekindex: één keer per wijziging van de pagina's, niet bij elke toets. Per tekst
 * staan zowel het origineel ({city}) als de ingevulde versie (Amsterdam) in kleine letters klaar.
 */
function buildIndex(pages: SearchablePage[]): Entry[] {
  const out: Entry[] = [];
  for (const p of pages) {
    const texts: {
      block: string | null;
      field: string;
      text: string;
    }[] = [];
    collect(p.value, null, '', texts);
    for (const t of texts) {
      if (!t.text.trim()) continue;
      const shown = fill(t.text);
      out.push({
        page: p.key,
        block: t.block,
        pageLabel: p.label,
        where: [t.block && humanize(t.block), t.field && t.field !== t.block && humanize(t.field)].filter(Boolean).join(' › '),
        text: shown,
        haystack: `${t.text}\n${shown}`.toLowerCase()
      });
    }
  }
  return out;
}

/**
 * "Waar pas ik dit aan?" Sarah ziet een zin op de site en wil hem veranderen. Typ een stukje,
 * en je springt naar het juiste blok. Zoekt ook in de ingevulde versie, dus "Amsterdam" vindt {city}.
 *
 * Snel bij grote pagina's: de index wordt alleen opnieuw gebouwd als de teksten veranderen, en
 * het filteren loopt via `useDeferredValue`. Het invoerveld reageert dus altijd meteen; de
 * resultaten volgen zodra de browser tijd heeft, zonder kunstmatige vertraging.
 */
export function PageTextSearch({
  pages,
  onPick
}: Props) {
  const [query, setQuery] = useState('');
  const deferred = useDeferredValue(query);
  const q = deferred.trim().toLowerCase();
  const open = query.trim().length >= MIN_QUERY;
  const stale = query !== deferred;
  const listId = useId();
  const index = useMemo(() => buildIndex(pages), [pages]);
  const matches = useMemo<Match[]>(() => {
    if (q.length < MIN_QUERY) return [];
    const found: Match[] = [];
    for (const e of index) {
      if (!e.haystack.includes(q)) continue;
      found.push(e);
      if (found.length >= MAX_RESULTS) break;
    }
    return found;
  }, [index, q]);
  return <div className="relative" data-id="element-3626" data-mp="3q0ryndkq1sss">
      <label className="relative block" data-id="element-3627" data-mp="3l309usfdimst">
        <span className="sr-only" data-id="element-3628" data-mp="3l41hemx6kr5s">Zoek een tekst op de site</span>
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-3629" data-mp="3g586n29a3e9b" />
        <input type="search" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Escape' && setQuery('')} placeholder="Zoek een zin die je op de site ziet" aria-controls={open ? listId : undefined} className={`${inputCls} pl-9`} data-id="element-3630" data-mp="hkqe7wc8wmco" />
      </label>
      {/* Aantal resultaten voor schermlezers, zonder elke toets voor te lezen. */}
      <p className="sr-only" aria-live="polite" data-id="element-3631" data-mp="2nf9kbemgxv8c">
        {open && !stale ? matches.length ? plural(matches.length, 'resultaat', 'resultaten') : 'Niets gevonden' : ''}
      </p>
      {open && <div id={listId} className={`mt-2 rounded-md border border-line bg-white shadow-sm transition-opacity duration-150 ease-soft ${stale ? 'opacity-70' : ''}`} data-id="element-3632" data-mp="kvt3i5dwlzju">
          {matches.length === 0 ? <p className="px-4 py-3 text-[13px] text-muted" data-id="element-3633" data-mp="b4onr3gemany">
              Niets gevonden. Staat het bij een product, behandeling of in Instellingen? Die teksten pas je daar aan.
            </p> : <ul className="divide-y divide-line" aria-label="Zoekresultaten" data-id="element-3634" data-mp="13t41lmnsumxp">
              {matches.map((m, i) => <li key={`${m.page}-${m.block}-${i}`} data-id="element-3635" data-mp="owcwqxvcwj0p" data-mpi={i}>
                  <button type="button" onClick={() => {
            onPick({
              page: m.page,
              block: m.block
            });
            setQuery('');
          }} className="block w-full px-4 py-3 text-left transition-colors duration-150 ease-soft hover:bg-shell" data-id="element-3636" data-mp="tvg3qnuwczbb">
                    <span className="block text-[12px] text-muted" data-id="element-3637" data-mp="1wqobw3mbmkx6">
                      {m.pageLabel}
                      {m.where && ` › ${m.where}`}
                    </span>
                    <span className="mt-0.5 line-clamp-2 block text-[13px] text-ink" data-id="element-3638" data-mp="25oq9e96uivfp">{m.text}</span>
                  </button>
                </li>)}
            </ul>}
          <button type="button" onClick={() => setQuery('')} className="flex w-full items-center justify-center gap-1.5 border-t border-line py-2 text-[12px] text-muted hover:text-ink" data-id="element-3639" data-mp="1dyviw9794zpg">
            <XIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-3640" data-mp="1ms8jlxbk55m3" />
            Sluiten
          </button>
        </div>}
    </div>;
}